import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelize } from 'src/utils/decamelize';

import { Trainer, TransferResponse } from './_types';
import { denormalizeTrainer, normalizeTrainer } from './normalizers';
import { parseHqAssignmentValue } from './utils';

// Re-export types for convenience
export type { Trainer, TransferResponse };

export const fetchCurrentTrainer = async (): Promise<Trainer> => {
  const response = await api.get(`/api/v4/trainers/current`);
  const normalized = normalizeTrainer(response.data);
  if (!normalized) throw new Error('Failed to normalize trainer data');
  return normalized;
};

export const fetchAllTrainers = async (
  params: Record<string, any> = {}
): Promise<Trainer[]> => {
  const response = await api.get(`/api/v3/trainers`, {
    params: {
      fetch_all: true,
      ...params,
    },
  });
  return response.data.map((t: any) => normalizeTrainer(t)).filter(Boolean);
};

export interface FetchTrainersOptions {
  page?: number;
  per?: number;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  withoutIds?: number[];
}

export interface FetchTrainersResponse {
  trainers: Trainer[];
  total: number;
}

export const fetchTrainers = async ({
  page = 1,
  per = 10,
  search = '',
  sortBy = '',
  sortDir = '',
}: FetchTrainersOptions): Promise<FetchTrainersResponse> => {
  const response = await api.get(`/api/v3/trainers`, {
    params: {
      page,
      per,
      'q[trainer_nickname_or_trainer_first_name_or_trainer_last_name_or_trainer_email_cont]':
        search,
      'q[s]': sortBy ? `${decamelize(sortBy || '')} ${sortDir || ''}` : '',
    },
  });
  return {
    trainers: response.data.trainer
      .map((t: any) => normalizeTrainer(t))
      .filter(Boolean),
    total: response.data?.meta?.total,
  };
};

export const fetchSubTrainers = async ({
  page = 1,
  per = 10,
  search = '',
  sortBy = '',
  sortDir = '',
  withoutIds = [],
}: FetchTrainersOptions): Promise<FetchTrainersResponse> => {
  const params: Record<string, any> = {
    sub_trainers: true,
    page,
    per,
  };

  if (search) {
    const splitSearch = search.trim().split(' ');
    if (splitSearch.length === 1) {
      params[
        'q[nickname_or_first_name_or_last_name_or_email_or_phone_number_cont]'
      ] = splitSearch[0];
    } else {
      params['q[first_name_cont]'] = splitSearch[0];
      params['q[last_name_cont]'] = splitSearch[1];
    }
  }

  if (sortBy) {
    params['q[s]'] = `${decamelize(sortBy)} ${sortDir}`;
  }

  if (withoutIds && withoutIds.length > 0) {
    params['without_ids'] = withoutIds;
  }

  const response = await api.get(`/api/v2/users`, { params });

  return {
    trainers: response.data.user
      .map((t: any) => normalizeTrainer(t))
      .filter(Boolean),
    total: response.data?.meta?.total,
  };
};

export interface DeleteTrainerOptions {
  trainerId: Trainer['id'];
  deleteAccount?: boolean;
}

export const deleteTrainer = async ({
  trainerId,
  deleteAccount = false,
}: DeleteTrainerOptions): Promise<Trainer> => {
  const response = await api.delete(`/api/v3/trainers/${trainerId}`, {
    data: { delete_account: deleteAccount },
  });
  const normalized = normalizeTrainer(response.data);
  if (!normalized) throw new Error('Failed to normalize trainer data');
  return normalized;
};

export interface FetchTrainerOptions {
  id: Trainer['id'];
}

export const fetchTrainer = async ({
  id,
}: FetchTrainerOptions): Promise<Trainer> => {
  const response = await api.get(`/api/v4/users/${id}`);
  const normalized = normalizeTrainer(response.data);
  if (!normalized) throw new Error('Failed to normalize trainer data');
  return normalized;
};

export const transferToTrainer = async (
  trainerId: Trainer['id'],
  amount: number,
  memo: string
): Promise<TransferResponse> => {
  const response = await api.post(`/api/v4/trainers/${trainerId}/transfer_to`, {
    amount,
    memo,
  });

  return response.data;
};

export const createTrainer = async (trainer: Trainer): Promise<Trainer> => {
  const response = await api.post(`/api/v3/trainers`, {
    trainer: denormalizeTrainer(trainer),
  });
  const normalized = normalizeTrainer(response.data);
  if (!normalized) throw new Error('Failed to normalize trainer data');
  return normalized;
};

export interface FetchTrainerByTrainerIdOptions {
  trainerId: Trainer['id'];
}

export const fetchTrainerByTrainerId = async ({
  trainerId,
}: FetchTrainerByTrainerIdOptions): Promise<Trainer> => {
  const response = await api.get(`/api/v3/trainers/${trainerId}`);
  const normalized = normalizeTrainer(response.data);
  if (!normalized) throw new Error('Failed to normalize trainer data');
  return normalized;
};

export const updateTrainer = async (
  trainerId: Trainer['id'],
  trainer: Partial<Trainer>
): Promise<Trainer> => {
  // Remove id from trainer object before sending to API

  const { id, ...trainerWithoutId } = trainer;

  const response = await api.put(`/api/v3/trainers/${trainerId}`, {
    trainer: {
      ...(denormalizeTrainer(trainerWithoutId) as Record<string, any>),
      landing_items: trainer.landingItems,
    },
  });
  const normalized = normalizeTrainer(response.data);
  if (!normalized) throw new Error('Failed to normalize trainer data');
  return normalized;
};

export const resendTrainerInvite = async (
  trainerId: Trainer['id']
): Promise<void> => {
  await api.post(`/api/v3/trainers/${trainerId}/resend_invite`);
};

export const addTrainerToGroup = async (
  trainerId: Trainer['id'],
  groupId: Identifier
): Promise<void> => {
  await api.get(`/api/v3/trainers/${trainerId}/add_to_group/${groupId}/`);
};

export const removeTrainerFromGroup = async (
  trainerId: Trainer['id'],
  groupId: Identifier
): Promise<void> => {
  await api.get(`/api/v3/trainers/${trainerId}/remove_from_group/${groupId}/`);
};

export const updateTrainerHqAssignment = async (
  trainerId: Trainer['id'],
  hqAssignmentValue: string
): Promise<Trainer> => {
  const { hqAssignmentType, hqAssignmentId } =
    parseHqAssignmentValue(hqAssignmentValue);

  return updateTrainer(trainerId, {
    hqAssignmentType,
    hqAssignmentId,
  });
};
